import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Typography,
  InputAdornment
} from '@mui/material';
import { Trash, PencilSimple, Plus } from '@phosphor-icons/react';
import api from '../../../utils/api';
import { DEFAULT_CRM_LEAD_SOURCES, mergeLeadSources } from '../../../utils/crmLeadSources';

const CRMSourcesManager = ({ open, onClose, sources = [], businessId, onSourcesUpdated }) => {
  const [localSources, setLocalSources] = useState(mergeLeadSources(sources));
  const [newSourceName, setNewSourceName] = useState('');
  const [renaming, setRenaming] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSources(mergeLeadSources(sources));
  }, [sources]);

  const handleAddSource = async () => {
    if (!newSourceName.trim() || !businessId) return;
    setSaving(true);
    try {
      const response = await api.post('/api/crm/sources', { label: newSourceName.trim(), businessId });
      const created = response.data?.source;
      setLocalSources((prev) => mergeLeadSources([...(prev || []), created]));
      setNewSourceName('');
      onSourcesUpdated?.();
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (sourceId, label) => {
    if (!label?.trim()) return;
    setSaving(true);
    try {
      const response = await api.put(`/api/crm/sources/${sourceId}`, { label: label.trim() });
      const updated = response.data?.source;
      setLocalSources((prev) =>
        (prev || []).map((source) => (source._id === sourceId ? { ...source, ...updated } : source))
      );
      setRenaming({});
      onSourcesUpdated?.();
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (sourceId) => {
    setSaving(true);
    try {
      await api.delete(`/api/crm/sources/${sourceId}`);
      setLocalSources((prev) => (prev || []).filter((source) => source._id !== sourceId));
      onSourcesUpdated?.();
    } finally {
      setSaving(false);
    }
  };

  const isDefaultSource = (source) => {
    return !source?._id || source.isLocked || DEFAULT_CRM_LEAD_SOURCES.some((item) => item.value === source.value);
  };

  const sortedSources = [...localSources]
    .filter((source) => !isDefaultSource(source))
    .sort((a, b) => {
    return String(a.label || '').localeCompare(String(b.label || ''));
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage Sources</DialogTitle>
      <DialogContent>
        <List>
          {!sortedSources.length && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.secondary">
                    No custom sources yet. Add one below.
                  </Typography>
                }
              />
            </ListItem>
          )}
          {sortedSources.map((source) => {
            return (
              <ListItem
                key={source._id || source.value}
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() =>
                        renaming[source._id] !== undefined
                          ? handleRename(source._id, renaming[source._id])
                          : setRenaming({ [source._id]: source.label })
                      }
                      disabled={saving}
                    >
                      <PencilSimple size={18} />
                    </IconButton>
                    <IconButton edge="end" size="small" onClick={() => handleArchive(source._id)} disabled={saving}>
                      <Trash size={18} />
                    </IconButton>
                  </Stack>
                }
              >
                {renaming[source._id] !== undefined ? (
                  <TextField
                    size="small"
                    value={renaming[source._id]}
                    onChange={(e) => setRenaming({ [source._id]: e.target.value })}
                    onBlur={() => handleRename(source._id, renaming[source._id])}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRename(source._id, renaming[source._id]);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <ListItemText
                    primary={<Typography variant="subtitle2">{source.label}</Typography>}
                    secondary={source.value}
                  />
                )}
              </ListItem>
            );
          })}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5, mb: 1 }}>
            <TextField
              placeholder="Add Custom Source"
              size="small"
              value={newSourceName}
              onChange={(e) => setNewSourceName(e.target.value)}
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddSource();
                }
              }}
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleAddSource}
                      disabled={saving || !newSourceName.trim()}
                      edge="end"
                      aria-label="Add source"
                    >
                      <Plus size={20} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Stack>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CRMSourcesManager;
